/**
 * Stage 36 — the USER-FACING contract for scenarios 6–7.
 *
 * Why this file exists next to `cross-unit.types.ts`: that file describes the technical
 * candidate catalog (clusters, alternatives, pairing strength, SHAP) which the drawer
 * still needs. This one describes what the analyst page shows, and it deliberately mirrors
 * the scenarios 1–5 case row so the same table cells and the same formatters work for both.
 *
 * The level vocabularies are IMPORTED from the scenarios 1–5 contract rather than copied:
 * a second copy is how two surfaces start calling `priority_review` different things.
 */
import {
  anomalyLevelLabels,
  ANOMALY_LEVEL_ORDER,
  mlLevelLabels,
  type AnomalyLevel,
  type MlLevel,
} from '../anomaly-cases-v3.types'
import type { TranslateFn } from '@/lib/i18n'

export { anomalyLevelLabels, ANOMALY_LEVEL_ORDER, mlLevelLabels }
export type { AnomalyLevel, MlLevel }

/** Scenarios this surface serves. 'all' shows 6 and 7 together. */
export type CrossUnitScenario = 6 | 7 | 'all'

/**
 * «Группа сравнения» for scenarios 6–7.
 *
 * IMPORTANT — what this is and is not. The anomaly model scores all of 6–7 in ONE cohort
 * (`cross_unit_broad_unified`), so there is no per-row model cohort to show. These groups
 * are built by the backend from two confirmed catalog columns — `shipment_leg.unit_type`
 * and `candidate_pair.limited_history` — and are descriptive only: they do not enter any
 * score and do not define the comparison the model actually made. Unknown codes fall back
 * to the raw value instead of being hidden.
 */
export function comparisonGroupLabels(t: TranslateFn): Record<string, string> {
  return {
    wagon_history: t('analytics.cu.group.wagonHistory'),
    container_history: t('analytics.cu.group.containerHistory'),
    wagon_container_history: t('analytics.cu.group.wagonContainerHistory'),
    mixed_unit_history: t('analytics.cu.group.mixedUnitHistory'),
    limited_history: t('analytics.cu.group.limitedHistory'),
    unit_unknown: t('analytics.cu.group.unitUnknown'),
  }
}

export function comparisonGroupLabel(code: string | null | undefined, t: TranslateFn): string {
  const c = String(code ?? '').trim()
  if (!c) return '—'
  return comparisonGroupLabels(t)[c] ?? c
}

/** One user-facing case row. Shape intentionally close to `AnomalyCaseRow` (scenarios 1–5). */
export interface CrossUnitCaseRow {
  /** `canonical_pair_id` — the case identity on this surface. */
  caseId: string
  /** `stable_case_group_id`. Searchable and shown in the drawer; never a main column. */
  clusterId: string | null
  scenarioId: number
  anomalyScore: number | null
  anomalyLevel: AnomalyLevel
  anomalyPercentile: number | null
  mlScore: number | null
  mlLevel: MlLevel
  mlRelativeDecile: number | null
  comparisonGroup: string
  limitedHistory: boolean
  cargo: string | null
  cargoExport: string | null
  cargoEtsng: string | null
  cargoGng: string | null
  /** Single user route: import origin → export destination, as in scenarios 1–5. */
  route: string | null
  importCountryFrom: string | null
  importCountryTo: string | null
  exportCountryFrom: string | null
  exportCountryTo: string | null
  importStationFrom: string | null
  importStationTo: string | null
  exportStationFrom: string | null
  exportStationTo: string | null
  importWeight: number | null
  exportWeight: number | null
  /** Always null here: the shared frontend formatter owns the whole weight decision tree. */
  weightDiffPct: number | null
  daysBetween: number | null
  importDate: string | null
  exportDate: string | null
  importRecreditDate: string | null
  importShipmentId: string | null
  exportShipmentId: string | null
  importWagon: string | null
  exportWagon: string | null
  importContainer: string | null
  exportContainer: string | null
  importUnitType: string | null
  exportUnitType: string | null
  pairingStatus: string | null
  isGroupRepresentative: boolean
  isExistingSelected: boolean
  matchScore: number | null
  /** Reason codes from the anomaly model's own explanation record. Never invented here. */
  anomalyReasons: string[]
}

export interface CrossUnitEligibilityAudit {
  schemaVersion?: string
  totals?: { sourceRows: number; eligibleRows: number; excludedRows: number }
  byScenario?: Record<string, {
    sourceRows: number
    eligibleRows: number
    excludedRows: number
    excludedByReason: Record<string, number>
    byAnomalyLevel: Record<string, number>
    byMlLevel: Record<string, number>
    byComparisonGroup: Record<string, number>
    levelSum: number
  }>
}

export interface CrossUnitCasesSummary {
  source: 'cross_unit_user_view'
  version: string | null
  scenarioId: number | null
  totalCases: number
  byAnomalyLevel: Record<string, number>
  byMlLevel: Record<string, number>
  byComparisonGroup: Record<string, number>
  /** Backend-computed sum of the four levels — the UI asserts it against `totalCases`. */
  levelSum: number
  reasonsWithCount: number
  reasonsWithoutCount: number
  distinctClusters: number
  eligibility?: CrossUnitEligibilityAudit
}

export interface CrossUnitCasesList {
  source: 'cross_unit_user_view'
  version: string | null
  total: number
  page: number
  pageSize: number
  totalPages: number
  sortBy: string
  sortDir: string
  paginatedInDatabase: boolean
  rows: CrossUnitCaseRow[]
}

/** Filters the user-facing surface accepts. Every one is resolved server-side. */
export interface CrossUnitCasesQuery {
  scenarioId?: CrossUnitScenario
  anomalyLevel?: string
  mlLevel?: string
  comparisonGroup?: string
  pairingStatus?: string
  limitedHistory?: boolean
  search?: string
  page?: number
  pageSize?: number
  sortBy?: string
  sortDir?: 'asc' | 'desc'
}

export const CROSS_UNIT_SORT_KEYS = [
  'anomalyScore',
  'mlScore',
  'daysBetween',
  'weightDiffPct',
  'exportDate',
  'importDate',
  'caseId',
] as const

export const CROSS_UNIT_DEFAULT_PAGE_SIZE = 50
